import { annualTaken, docState, endOfISODate } from "@core";
import type {
  CountryCode, DocKind, Employee, EmploymentTerms, LeaveRequest,
} from "@core";

import { supabase } from "./supabase";

/**
 * One person's file: the paperwork rather than the clock.
 *
 * The register and the payslip already answer "what did they work" and "what
 * does it come to". This answers the questions an employer gets asked at a
 * desk — what are they on, what were they on before, is their permit current,
 * how much leave is left.
 *
 * Read-only on purpose for now. Changing terms is the one action here that
 * rewrites what somebody is paid, and it belongs behind the same deliberate
 * sheet the app gives it rather than being slipped into a page of details.
 */

/** The database enum, which the UI does not speak. */
const FROM_DB: Record<string, DocKind> = {
  contract: "Contract",
  id_document: "ID document",
  work_permit: "Work permit",
  banking_details: "Banking details",
};

export interface StoredDoc {
  id: string;
  kind: DocKind;
  storagePath: string;
  fileName: string;
  validUntil: string | null;
  uploadedAt: string;
}

export interface DocView {
  /** Who opened it. A uuid: the dashboard has no RLS path to a name. */
  actor: string;
  action: string;
  at: string;
  documentId: string;
}

export interface PersonFile {
  employee: Employee;
  /** Newest first. The open one — no end date — is the current agreement. */
  terms: EmploymentTerms[];
  current: EmploymentTerms | null;
  docs: StoredDoc[];
  documents: ReturnType<typeof docState>;
  leave: LeaveRequest[];
  leaveTaken: number;
  /** Who has opened this person's documents, newest first. */
  views: DocView[];
  siteName: string;
}

interface EmployeeRow {
  id: string;
  org_id: string;
  site_id: string | null;
  user_id: string | null;
  full_name: string;
  phone: string | null;
  occupation: Employee["occupation"];
  occupation_other: string | null;
  nationality: string;
  started_on: string;
  status: string;
  ended_on: string | null;
  sites: { name: string } | { name: string }[] | null;
}

function toTerms(t: Record<string, any>): EmploymentTerms {
  return {
    id: t.id,
    employeeId: t.employee_id,
    validFrom: t.valid_from,
    validTo: t.valid_to,
    rate: t.rate_cents / 100,
    currency: t.currency,
    ordinaryDaily: Number(t.ordinary_daily),
    ordinaryWeekly: Number(t.ordinary_weekly),
    breakMins: t.break_mins,
    breakAfter: Number(t.break_after_h),
    ot: Number(t.ot_multiplier),
    sunday: Number(t.sunday_multiplier),
    holiday: Number(t.holiday_multiplier ?? 2),
    daysPerWeek: t.days_per_week,
    annualLeaveDays: Number(t.annual_leave_days),
    sickLeaveDays: Number(t.sick_leave_days),
    sickCycleMonths: t.sick_cycle_months,
    familyLeaveDays: Number(t.family_leave_days),
  };
}

export async function loadPerson(
  employeeId: string, country: CountryCode,
): Promise<PersonFile | null> {
  const { data: row } = await supabase
    .from("employees")
    .select("id, org_id, site_id, user_id, full_name, phone, occupation, occupation_other," +
            " nationality, started_on, status, ended_on, sites(name)")
    .eq("id", employeeId)
    .maybeSingle()
    // The embedded sites(name) defeats the generated row type, which then
    // widens to an error shape and takes every field with it.
    .returns<EmployeeRow>();
  if (!row) return null;

  const [termRes, docRes, leaveRes] = await Promise.all([
    supabase.from("employment_terms").select("*")
      .eq("employee_id", employeeId).order("valid_from", { ascending: false }),
    supabase.from("documents")
      .select("id, kind, storage_path, file_name, valid_until, uploaded_at")
      .eq("employee_id", employeeId).order("uploaded_at", { ascending: false }),
    supabase.from("leave_requests")
      .select("id, type, starts_on, ends_on, days, days_charged, days_note, reason, status, created_at")
      .eq("employee_id", employeeId).order("starts_on", { ascending: false }),
  ]);

  const docs: StoredDoc[] = ((docRes.data ?? []) as Record<string, any>[]).map((d) => ({
    id: d.id,
    kind: FROM_DB[d.kind] ?? "Contract",
    storagePath: d.storage_path,
    fileName: d.file_name,
    validUntil: d.valid_until,
    uploadedAt: d.uploaded_at,
  }));

  // Who has looked. Fetched after the documents, because it is keyed on them.
  let views: DocView[] = [];
  if (docs.length > 0) {
    const { data } = await supabase
      .from("document_access_log")
      .select("document_id, actor, action, at")
      .in("document_id", docs.map((d) => d.id))
      .order("at", { ascending: false })
      .limit(20);
    views = ((data ?? []) as Record<string, any>[]).map((v) => ({
      documentId: v.document_id, actor: v.actor, action: v.action, at: v.at,
    }));
  }

  const site = Array.isArray(row.sites) ? row.sites[0] : row.sites;

  const employee: Employee = {
    id: row.id,
    orgId: row.org_id,
    siteId: row.site_id,
    userId: row.user_id,
    fullName: row.full_name,
    phone: row.phone ?? "",
    occupation: row.occupation,
    occupationOther: row.occupation_other,
    nationality: row.nationality,
    startedOn: row.started_on,
    status: row.status,
    endsOn: row.ended_on ?? null,
    docs: docs.map((d) => ({
      id: d.id,
      employeeId: row.id,
      kind: d.kind,
      storagePath: d.storagePath,
      fileName: d.fileName,
      uploadedAt: Date.parse(d.uploadedAt),
      uploadedBy: "",
      validUntil: d.validUntil ? endOfISODate(d.validUntil) : null,
    })),
  } as Employee;

  const terms = ((termRes.data ?? []) as Record<string, any>[]).map(toTerms);
  const leave: LeaveRequest[] = ((leaveRes.data ?? []) as Record<string, any>[]).map((l) => ({
    id: l.id,
    type: l.type,
    startsOn: l.starts_on,
    endsOn: l.ends_on,
    days: Number(l.days),
    // What the employer actually charged, when they disagreed. Without this the
    // balance quietly reverts to the computed figure and an override the worker
    // was told about stops counting.
    daysCharged: l.days_charged == null ? undefined : Number(l.days_charged),
    daysNote: l.days_note ?? undefined,
    reason: l.reason ?? "",
    status: l.status,
    createdAt: l.created_at,
  }));

  return {
    employee,
    terms,
    current: terms.find((t) => t.validTo === null) ?? null,
    docs,
    documents: docState(employee, country),
    leave,
    leaveTaken: annualTaken(leave),
    views,
    siteName: (site as any)?.name ?? "",
  };
}

/**
 * A short-lived link to a document, and the log entry that pays for it.
 *
 * The link is made first and the log is written second, and the order matters
 * in both directions. Under POPIA an ID copy is personal information, and the
 * privacy policy promises the worker can see who opened theirs and when — so
 * if the log cannot be written, the file is not shown. But logging first meant
 * a failed read still left a "view" against somebody's name for a document
 * nobody ever saw, which is the same promise broken from the other end: a
 * worker asking "who opened my ID on the third" would be told about a view
 * that did not happen.
 *
 * Neither half is allowed to lie, so neither half happens without the other.
 */
export type OpenResult =
  | { ok: true; url: string }
  /** The file itself could not be reached. Nothing was recorded. */
  | { ok: false; reason: "UNREADABLE" }
  /** The file was there; the log entry was not written, so it was not opened. */
  | { ok: false; reason: "NOT_LOGGED" };

export async function openDocument(doc: StoredDoc): Promise<OpenResult> {
  const { data: session } = await supabase.auth.getSession();
  const actor = session.session?.user.id;
  if (!actor) return { ok: false, reason: "UNREADABLE" };

  const { data, error } = await supabase.storage
    .from("employee-docs")
    .createSignedUrl(doc.storagePath, 60);
  if (error || !data) return { ok: false, reason: "UNREADABLE" };

  const { error: logError } = await supabase
    .from("document_access_log")
    .insert({ document_id: doc.id, actor, action: "view" });
  if (logError) return { ok: false, reason: "NOT_LOGGED" };

  return { ok: true, url: data.signedUrl };
}
